import { APP_LANGUAGES } from '@iac/contracts';
import type { AppKey, Locale } from '@iac/contracts';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';
import { IconSignOut, IconUserCircle } from '../components/icons';
import { useAuth } from '../lib/auth';
import { shell } from '../i18n/shell';
import { useLocale } from '../lib/locale';

export interface AppCrumb {
  name: string;
  href: string;
}

interface CrumbValue {
  crumb: AppCrumb | null;
  setCrumb: (crumb: AppCrumb | null) => void;
}

const CrumbContext = createContext<CrumbValue>({
  crumb: null,
  setCrumb: () => undefined,
});

/**
 * Feature pages announce which app they belong to; the header renders it as a
 * breadcrumb and the UI language falls back to what that app supports. The
 * page owns the crumb rather than the layout hard-coding one per route.
 */
export function useAppCrumb(appKey: AppKey, name: string, href: string): void {
  const { setCrumb } = useContext(CrumbContext);
  const { setAppKey } = useLocale();
  useEffect(() => {
    setCrumb({ name, href });
    setAppKey(appKey);
    return () => {
      setCrumb(null);
      setAppKey(null);
    };
  }, [appKey, name, href, setCrumb, setAppKey]);
}

/** Binary by design: with a third language this becomes a menu. */
function otherLocale(locale: Locale): Locale {
  return locale === 'pl' ? 'en' : 'pl';
}

/**
 * Labelled with the language a click switches to. Inside an app that has only
 * one language it names the missing one and stays inert, with a hint.
 */
function LocaleToggle() {
  const { locale, preference, appKey, setPreference } = useLocale();
  const t = shell[preference];
  // Every app has both languages today; widened so the check stays for one
  // that ships with a single language.
  const languages: readonly Locale[] | null =
    appKey === null ? null : APP_LANGUAGES[appKey];
  const fixed = languages !== null && languages.length === 1;
  const target = otherLocale(locale);
  const label = fixed ? t.onlyIn[locale] : t.switchTo[target];

  return (
    <button
      type="button"
      onClick={() => {
        if (!fixed) setPreference(target);
      }}
      className="btn-icon-ghost header-locale"
      aria-disabled={fixed}
      aria-label={label}
      title={label}
    >
      {target.toUpperCase()}
    </button>
  );
}

export function AppLayout() {
  const { session, signOut } = useAuth();
  const { preference } = useLocale();
  // The header belongs to the shell, which has both languages, so it follows
  // the preference even inside an app that falls back.
  const t = shell[preference];
  const [crumb, setCrumb] = useState<AppCrumb | null>(null);
  const value = useMemo<CrumbValue>(() => ({ crumb, setCrumb }), [crumb]);

  return (
    <CrumbContext.Provider value={value}>
      <header className="app-header">
        <div className="app-header-inner">
          <div className="header-brand">
            {/* Both variants render and CSS shows one: the mark on a phone,
                the wordmark from `sm` up. The label keeps the name stable. */}
            <Link to="/apps" className="header-home" aria-label="ITLamb">
              <span className="header-home-mark">
                <BrandMark />
              </span>
              <span className="wordmark header-home-wordmark">
                IT<span className="wordmark-accent">Lamb</span>
              </span>
            </Link>
            {crumb && (
              <>
                <span className="breadcrumb-sep" aria-hidden="true">
                  ›
                </span>
                <Link to={crumb.href} className="breadcrumb-app">
                  {crumb.name}
                </Link>
              </>
            )}
          </div>
          <div className="header-actions">
            <LocaleToggle />
            {session && (
              <span className="header-login" title={session.user.login}>
                {session.user.login}
              </span>
            )}
            <Link
              to="/profile"
              className="btn-icon-ghost"
              aria-label={t.profile}
              title={t.profile}
            >
              <IconUserCircle />
            </Link>
            <button
              type="button"
              onClick={() => void signOut()}
              className="btn-icon-ghost"
              aria-label={t.signOut}
              title={t.signOut}
            >
              <IconSignOut />
            </button>
          </div>
        </div>
      </header>
      <main className="app-main">
        <Outlet />
      </main>
    </CrumbContext.Provider>
  );
}
